import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import { IconType } from 'react-icons';
import { classNames } from '@wajeez-web/core/utils';
import { CommonDispatch, useCommonDispatch } from '@wajeez-web/stores/common';
import { CommonActions } from '@wajeez-web/stores/common';
import { TabsProps } from './Tabs';

interface TabsWithHeadersProps extends TabsProps {
  header: string;
  tabs: {
    text: string;
    desc?: string;
    disabled: boolean;
    Icon?: IconType;
  }[];
}

const SidebarContainer = ({
  tabs,
  header,
  selectedTab,
  setSelectedTab,
}: TabsWithHeadersProps) => {
  return (
    <div className="flex  h-full w-full bg-white border  ">
      <div className="flex flex-col w-full items-center h-full overflow-hidden text-textPrimary dark bg-white print-hidden ">
        <div className="group  flex items-center  border-b w-full cursor-pointer px-6 py-10 sm:px-10 font-extrabold text-3xl  ">
          {header}
        </div>

        <div className="w-full  justify-between h-full flex flex-col    hover:scrollbar-thumb-gray-700 scrollbar-track-transparent scrollbar-thin">
          <div className="flex flex-col items-center w-full  border-gray-700">
            <nav className="  w-full  ">
              {tabs.map(
                (item, i) =>
                  !item.disabled && (
                    <div key={item.text + i}>
                      {
                        <div key={i}>
                          <div
                            onClick={() => setSelectedTab(i)}
                            key={item.text}
                            className={classNames(
                              i === selectedTab
                                ? ` text-primary bg-primary-50 `
                                : ` text-textPrimary hover:bg-gray-100 `,
                              '  border-b space-y-1 w-full cursor-pointer p-6 sm:py-8 sm:px-10  text-base  '
                            )}
                          >
                            <div className="group  flex items-center">
                              {item.Icon && (
                                <item.Icon
                                  className={classNames(
                                    i === selectedTab
                                      ? 'text-primary'
                                      : 'text-textSecondary',
                                    'me-3 h-6 w-6'
                                  )}
                                  aria-hidden="true"
                                />
                              )}
                              {item.text}
                            </div>
                            <div className="text-sm text-textSecondary ms-9">
                              {item.desc}
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  )
              )}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

const TabsWithHeaders = ({
  header,
  tabs,
  selectedTab,
  setSelectedTab,
}: TabsWithHeadersProps) => {
  const dispatch = useCommonDispatch();
  return (
    <div className="w-full">
      <Transition.Root show={false} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 flex z-40 md:hidden"
          open={false}
          onClose={() => dispatch(CommonActions.setNavBarCollapsed(true))}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600/75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative  flex flex-col max-w-xs bg-gradient-to-b from-sidebar-bg-from to-sidebar-bg-to">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className={`absolute top-0  ltr:right-0 rtl:left-0 `}>
                  <button
                    className="ms-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none "
                    onClick={() =>
                      dispatch(CommonActions.setNavBarCollapsed(false))
                    }
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XIcon
                      className="h-6 w-6 text-gray-400"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </Transition.Child>

              <div className="flex  h-screen  bg-gradient-to-b from-sidebar-bg-from to-sidebar-bg-to py-3 ">
                <SidebarContainer
                  header={header}
                  tabs={tabs}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                />
              </div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex flex-col flex-grow md:flex-shrink-0 h-full  w-full">
        <SidebarContainer
          header={header}
          tabs={tabs}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      </div>
    </div>
  );
};

export default TabsWithHeaders;
